import type { SubmitHandler } from 'react-hook-form';
import { useCallback, useRef, useState } from 'react';
import { Button, Snackbar } from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import { ContentBlock } from '~/components/molecules/ContentBlock';
import { ErrorMessage } from '~/components/molecules/ErrorMessage';
import { copyToClipboard } from '~/helpers/copyToClipboard';

const SNACKBAR_AUTO_HIDE_DURATION = 1000;

export type IArticleListItemCopyShareLinkDialogFormValues = {
  title?: string;
  content?: string;
  target?: string;
  targetValue?: 'feed' | 'article' | 'userArticle' | 'chatRoomId' | 'url';
  hideNavbar?: boolean;
};

export type IArticleListItemCopyShareLinkDialogFormProps = {
  onSubmit?: SubmitHandler<IArticleListItemCopyShareLinkDialogFormValues>;
  getShareUrl: () => Promise<string | null>;
};

export const ArticleListItemCopyShareLinkDialogForm: React.FC<
  IArticleListItemCopyShareLinkDialogFormProps
> = (props) => {
  const { getShareUrl } = props;

  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState<string>('');
  const [shareError, setShareError] = useState<null | string>(null);

  const shareUrlNodeRef = useRef<HTMLTextAreaElement | null>(null);

  const { t } = useTranslation();

  const generateOrGetShareUrl = useCallback(() => {
    setLoading(true);
    getShareUrl()
      .then((newUrl) => {
        setShareUrl(newUrl || '');
      })
      .catch((e) => {
        setShareError(`${e}`);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [getShareUrl]);

  const internalCopyToClipboard = useCallback(() => {
    if (!shareUrlNodeRef.current) {
      return;
    }

    shareUrlNodeRef.current.focus();
    shareUrlNodeRef.current.select();

    try {
      document.execCommand('copy');
    } finally {
      copyToClipboard(shareUrlNodeRef.current.value).catch(() => {});
      setCopied(true);
    }
  }, []);

  if (loading) {
    return (
      <ContentBlock.Body>
        {t('ArticleListItemCopyShareLinkDialog.loadingLink')}
      </ContentBlock.Body>
    );
  } else if (shareError) {
    return (
      <ContentBlock.Body>
        <ErrorMessage error={shareError} />
      </ContentBlock.Body>
    );
  } else if (!shareUrl) {
    return (
      <ContentBlock.Body>
        <Button disabled={loading} onClick={generateOrGetShareUrl}>
          {t('ArticleListItemCopyShareLinkDialog.generateShareUrl')}
        </Button>
      </ContentBlock.Body>
    );
  } else {
    return (
      <ContentBlock.Body>
        <div>
          <textarea
            style={{ resize: 'none', height: '2em', width: 'auto', padding: 0 }}
            readOnly
            ref={shareUrlNodeRef}
            value={shareUrl}
          />
          <Button disabled={loading} onClick={internalCopyToClipboard}>
            {t('ArticleListItemCopyShareLinkDialog.copyLinkButton')}
          </Button>
        </div>

        <Snackbar
          open={copied}
          onClose={() => setCopied(false)}
          autoHideDuration={SNACKBAR_AUTO_HIDE_DURATION}
        >
          {t('ArticleListItemCopyShareLinkDialog.copySuccessSnackbar')}
        </Snackbar>
      </ContentBlock.Body>
    );
  }
};
